import { useState, useCallback } from 'react';
import { Plus, Trash2, X, ChevronDown } from 'lucide-react';
import {
  FilterConfig,
  FilterCondition,
  FilterGroup,
  FilterField,
  ConditionOperator,
  isFilterGroup,
  FILTER_FIELD_LABELS,
  FIELD_OPERATORS,
  OPERATOR_LABELS,
  BOOLEAN_FIELDS,
  PRIORITY_VALUES,
  DATE_WITHIN_PRESETS,
} from '../../types/filter';
import { cn } from '../../utils/cn';

interface FilterBuilderProps {
  value: FilterConfig;
  onChange: (config: FilterConfig) => void;
  categories?: string[];
}

export default function FilterBuilder({ value, onChange, categories = [] }: FilterBuilderProps) {
  return (
    <div className="space-y-3">
      <FilterGroupEditor
        group={value}
        onChange={onChange}
        onDelete={() => {}}
        isRoot={true}
        categories={categories}
      />
    </div>
  );
}

interface FilterGroupEditorProps {
  group: FilterGroup;
  onChange: (group: FilterGroup) => void;
  onDelete: () => void;
  isRoot?: boolean;
  categories: string[];
}

function FilterGroupEditor({ group, onChange, onDelete, isRoot = false, categories }: FilterGroupEditorProps) {
  const toggleOperator = () => {
    onChange({
      ...group,
      operator: group.operator === 'AND' ? 'OR' : 'AND',
    });
  };

  const addCondition = () => {
    const newCondition: FilterCondition = {
      field: 'sender',
      operator: 'contains',
      value: '',
    };
    onChange({
      ...group,
      conditions: [...group.conditions, newCondition],
    });
  };

  const addGroup = () => {
    const newGroup: FilterGroup = {
      operator: group.operator === 'AND' ? 'OR' : 'AND',
      conditions: [],
    };
    onChange({
      ...group,
      conditions: [...group.conditions, newGroup],
    });
  };

  const updateCondition = (index: number, updated: FilterCondition | FilterGroup) => {
    const newConditions = [...group.conditions];
    newConditions[index] = updated;
    onChange({ ...group, conditions: newConditions });
  };

  const deleteCondition = (index: number) => {
    onChange({
      ...group,
      conditions: group.conditions.filter((_, i) => i !== index),
    });
  };

  return (
    <div
      className={cn(
        'rounded-lg p-3',
        isRoot ? '' : 'ml-4 border-l-2',
      )}
      style={{
        backgroundColor: isRoot ? 'transparent' : 'var(--bg-secondary)',
        borderColor: group.operator === 'AND' ? 'var(--brand-primary)' : 'var(--color-warning)',
      }}
    >
      {/* Operator Toggle */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={toggleOperator}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: group.operator === 'AND' 
              ? 'rgba(99, 102, 241, 0.15)' 
              : 'rgba(234, 179, 8, 0.15)',
            color: group.operator === 'AND' 
              ? 'var(--brand-primary)' 
              : 'var(--color-warning)',
          }}
        >
          {group.operator === 'AND' ? 'UND' : 'ODER'}
          <ChevronDown className="w-3 h-3" />
        </button>
        
        {!isRoot && (
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg transition-colors text-error hover:bg-error/10"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        {group.conditions.map((cond, index) => (
          <div key={index}>
            {isFilterGroup(cond) ? (
              <FilterGroupEditor
                group={cond}
                onChange={(updated) => updateCondition(index, updated)}
                onDelete={() => deleteCondition(index)}
                categories={categories}
              />
            ) : (
              <FilterConditionEditor
                condition={cond}
                onChange={(updated) => updateCondition(index, updated)}
                onDelete={() => deleteCondition(index)}
                categories={categories}
              />
            )}
          </div>
        ))}
      </div>

      {/* Add Buttons */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={addCondition}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
        >
          <Plus className="w-3 h-3" />
          Bedingung
        </button>
        <button
          onClick={addGroup}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-secondary)',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
        >
          <Plus className="w-3 h-3" />
          Gruppe
        </button>
      </div>
    </div>
  );
}

interface FilterConditionEditorProps {
  condition: FilterCondition;
  onChange: (condition: FilterCondition) => void;
  onDelete: () => void;
  categories: string[];
}

function FilterConditionEditor({ condition, onChange, onDelete, categories }: FilterConditionEditorProps) {
  const availableOperators = FIELD_OPERATORS[condition.field] || ['contains'];
  const isBoolean = BOOLEAN_FIELDS.includes(condition.field);
  const isPriority = condition.field === 'priority';
  const isCategory = condition.field === 'category';
  const isDate = condition.field === 'date';
  const isWithin = condition.operator === 'within';

  const handleFieldChange = (field: FilterField) => {
    const ops = FIELD_OPERATORS[field];
    const newOp = ops.includes(condition.operator) ? condition.operator : ops[0];
    
    // Reset value based on field type
    let newValue: string | boolean | number = '';
    if (BOOLEAN_FIELDS.includes(field)) {
      newValue = true;
    } else if (field === 'priority') {
      newValue = 'normal';
    } else if (field === 'date' && newOp === 'within') {
      newValue = 7;
    }
    
    onChange({
      ...condition,
      field,
      operator: newOp,
      value: newValue,
    });
  };

  const handleOperatorChange = (operator: ConditionOperator) => {
    let newValue = condition.value;
    if (operator === 'within' && condition.field === 'date') {
      newValue = 7;
    }
    onChange({ ...condition, operator, value: newValue });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 p-2 rounded-lg"
      style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-default)' }}
    >
      {/* Negate Toggle */}
      <button
        onClick={() => onChange({ ...condition, negate: !condition.negate })}
        className={cn(
          'px-2 py-1 rounded text-xs font-medium transition-colors',
          condition.negate 
            ? 'bg-error/15 text-error' 
            : 'bg-transparent text-text-tertiary hover:bg-bg-hover'
        )}
      >
        NICHT
      </button>

      {/* Field Select */}
      <select
        value={condition.field}
        onChange={(e) => handleFieldChange(e.target.value as FilterField)}
        className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
        style={{ color: 'var(--text-primary)' }}
      >
        {Object.entries(FILTER_FIELD_LABELS).map(([field, label]) => (
          <option key={field} value={field}>{label}</option>
        ))}
      </select>

      {/* Operator Select */}
      <select
        value={condition.operator}
        onChange={(e) => handleOperatorChange(e.target.value as ConditionOperator)}
        className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
        style={{ color: 'var(--text-primary)' }}
      >
        {availableOperators.map((op) => (
          <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
        ))}
      </select>

      {/* Value Input */}
      {isBoolean ? (
        <select
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value === 'true' })}
          className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
          style={{ color: 'var(--text-primary)' }}
        >
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      ) : isPriority ? (
        <select
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
          style={{ color: 'var(--text-primary)' }}
        >
          {PRIORITY_VALUES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      ) : isCategory && categories.length > 0 ? (
        <select
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default min-w-[120px]"
          style={{ color: 'var(--text-primary)' }}
        >
          <option value="">Kategorie wählen...</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      ) : isDate && isWithin ? (
        <select
          value={Number(condition.value)}
          onChange={(e) => onChange({ ...condition, value: parseInt(e.target.value) })}
          className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
          style={{ color: 'var(--text-primary)' }}
        >
          {DATE_WITHIN_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      ) : isDate ? (
        <input
          type="date"
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
          style={{ color: 'var(--text-primary)' }}
        />
      ) : (
        <input
          type="text"
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          placeholder="Wert eingeben..."
          className="flex-1 min-w-[120px] px-2 py-1.5 rounded-lg text-sm bg-bg-secondary border border-border-default"
          style={{ color: 'var(--text-primary)' }}
        />
      )}

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="p-1.5 rounded-lg transition-colors text-text-tertiary hover:text-error hover:bg-error/10"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Helper to create empty filter config
export function createEmptyFilterConfig(): FilterConfig {
  return {
    operator: 'AND',
    conditions: [],
  };
}

// Helper to check if filter has any conditions
export function hasConditions(config: FilterConfig): boolean {
  return config.conditions.length > 0;
}
